import React, { useState, useEffect } from "react";
import { getHRAs, postAddHRA } from "../../../ApiHelper/url_Helpers";
import { toasterError, toasterSuccess } from "../../../Components/toaster";

const HRAForm = ({ disabled }) => {
  const [hras, setHras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    from_month: "4",
    to_month: "7",
    monthly_rent: "15000",
    city_type: "",
    landlord_name: "",
    landlord_pan: "",
  });

  useEffect(() => {
    fetchHRAs();
  }, []);

  const fetchHRAs = async () => {
    try {
      setLoading(true);
      const response = await getHRAs();
      if (response && response.data) {
        setHras(response.data);
      }
    } catch (error) {
      console.error("Error fetching HRAs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddHRA = async () => {
    try {
      setSubmitting(true);
      await postAddHRA(formData);
      toasterSuccess("HRA Added successfully");
      fetchHRAs(); // Refresh the list
      // Reset form if needed, or keep it depending on UX requirements
    } catch (error) {
      console.error("Error adding HRA:", error);
      toasterError("Failed to add HRA");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm p-6 border border-gray-100 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">House Rent (HRA)</h2>
        {loading && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
      
      <div className="grid grid-cols-12 gap-4">
        {/* Left Side (List of HRAs) */}
        <div className="col-span-12 md:col-span-4">
          <div className="grid grid-cols-3 text-xs font-semibold text-gray-500 mb-2 uppercase tracking-wide">
            <div>Period</div>
            <div>Rent P.M.</div>
            <div>City</div>
          </div>
          
          {hras.length === 0 && !loading && (
            <div className="text-sm text-gray-400 mt-4">No HRAs added yet.</div>
          )}

          {hras.map((hra, idx) => (
            <div key={idx} className="grid grid-cols-3 text-sm text-gray-800 mb-4 pb-4 border-b border-gray-100">
              <div>{hra.from_month}-{hra.to_month}</div>
              <div>₹{hra.monthly_rent}</div>
              <div className="capitalize">{hra.city_type || "N/A"}</div>
            </div>
          ))}
        </div>

        {/* Right Side (Form to Add/Edit HRA) */}
        <div className="col-span-12 md:col-span-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From month (1-12)</label>
              <input type="text" name="from_month" value={formData.from_month} onChange={handleInputChange} className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" disabled={disabled} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To month (1-12)</label>
              <input type="text" name="to_month" value={formData.to_month} onChange={handleInputChange} className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" disabled={disabled} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Monthly rent</label>
              <input type="text" name="monthly_rent" value={formData.monthly_rent} onChange={handleInputChange} className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" disabled={disabled} />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">City type</label>
              <select name="city_type" value={formData.city_type} onChange={handleInputChange} className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" disabled={disabled}>
                <option value="">- Select an option -</option>
                <option value="metro">Metro</option>
                <option value="non-metro">Non-Metro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Landlord name</label>
              <input type="text" name="landlord_name" value={formData.landlord_name} onChange={handleInputChange} className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" disabled={disabled} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Landlord PAN</label>
              <input type="text" name="landlord_pan" value={formData.landlord_pan} onChange={handleInputChange} className="w-full border border-gray-200 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500" disabled={disabled} />
            </div>
          </div>
          
          <div className="flex justify-end mt-2">
            <button 
              type="button" 
              onClick={handleAddHRA}
              className="text-blue-600 text-sm font-medium hover:underline disabled:opacity-50" 
              disabled={disabled || submitting}
            >
              {submitting ? "Adding..." : "+ Add Another HRA"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRAForm;
